import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/feature/AppLayout';
import { useProducts, type Product } from '@/hooks/useProducts';
import { useAuthContext } from '@/contexts/AuthContext';

const categories = ['All', 'Payments', 'Identity', 'Crypto', 'Messaging', 'Data', 'AI/ML'];

export default function ProductsPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const { products, loading, createProduct, updateProduct, deleteProduct, toggleProductStatus } = useProducts();
  const [filter, setFilter] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [form, setForm] = useState({ name: '', description: '', category: 'Payments', price_1_2_year: '', price_3_5_year: '', price_lifetime: '' });
  const [saving, setSaving] = useState(false);

  const filtered = filter === 'All' ? products : products.filter(p => p.category === filter);

  const openAdd = () => {
    setEditProduct(null);
    setForm({ name: '', description: '', category: 'Payments', price_1_2_year: '', price_3_5_year: '', price_lifetime: '' });
    setShowModal(true);
  };

  const openEdit = (p: Product) => {
    setEditProduct(p);
    setForm({ name: p.name, description: p.description, category: p.category, price_1_2_year: String(p.price_1_2_year), price_3_5_year: String(p.price_3_5_year), price_lifetime: String(p.price_lifetime) });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (editProduct) {
      await updateProduct(editProduct.id, {
        name: form.name,
        description: form.description,
        category: form.category,
        price_1_2_year: Number(form.price_1_2_year),
        price_3_5_year: Number(form.price_3_5_year),
        price_lifetime: Number(form.price_lifetime),
      });
    } else {
      await createProduct({
        name: form.name,
        description: form.description,
        category: form.category,
        price_1_2_year: Number(form.price_1_2_year),
        price_3_5_year: Number(form.price_3_5_year),
        price_lifetime: Number(form.price_lifetime),
        status: 'active',
        requests_per_month: 100000,
        uptime: '99.9%',
        endpoints: 10,
        icon: 'ri-code-line',
        color: '#ff7f0a',
      });
    }
    setSaving(false);
    setShowModal(false);
  };

  const handleDelete = async (id: string) => { await deleteProduct(id); };
  const handleToggle = async (id: string, status: string) => { await toggleProductStatus(id, status); };

  return (
    <AppLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${filter === c ? 'bg-sidebar text-white' : 'bg-white border border-surface-3 text-gray-500 hover:border-gray-300'}`}>{c}</button>
            ))}
          </div>
          {isAdmin && (
            <button onClick={openAdd} className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line" /> Add Product
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-5 animate-pulse">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-32" />
                    <div className="h-3 bg-gray-100 rounded w-20" />
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded mb-2" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-xl p-5 flex flex-col gap-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: (p.color || '#ff7f0a') + '18' }}>
                      <i className={`${p.icon || 'ri-code-line'} text-xl`} style={{ color: p.color || '#ff7f0a' }} />
                    </span>
                    <div>
                      <div className="font-semibold text-sm text-gray-900">{p.name}</div>
                      <span className="text-[11px] bg-surface-2 text-gray-500 px-2 py-0.5 rounded-full">{p.category}</span>
                    </div>
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{p.status}</span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">{p.description}</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[['1-2yr', p.price_1_2_year], ['3-5yr', p.price_3_5_year], ['Lifetime', p.price_lifetime]].map(([label, val]) => (
                    <div key={String(label)} className="bg-surface-2 rounded-lg py-2">
                      <div className="text-xs font-semibold text-gray-900">${val}</div>
                      <div className="text-[10px] text-gray-400">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-400">
                  <span><i className="ri-terminal-line mr-1" />{p.endpoints} endpoints</span>
                  <span><i className="ri-wifi-line mr-1" />{p.uptime}</span>
                  <span><i className="ri-stack-line mr-1" />{(p.requests_per_month / 1000).toFixed(0)}k/mo</span>
                </div>
                {isAdmin && (
                  <div className="flex gap-2 pt-1 border-t border-surface-3">
                    <button onClick={() => openEdit(p)} className="flex-1 text-xs py-1.5 rounded-lg border border-surface-3 hover:bg-surface-2 text-gray-600 cursor-pointer whitespace-nowrap transition-colors"><i className="ri-edit-line mr-1" />Edit</button>
                    <button onClick={() => handleToggle(p.id, p.status)} className={`flex-1 text-xs py-1.5 rounded-lg border cursor-pointer whitespace-nowrap transition-colors ${p.status === 'active' ? 'border-amber-200 text-amber-600 hover:bg-amber-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>{p.status === 'active' ? 'Disable' : 'Enable'}</button>
                    <button onClick={() => handleDelete(p.id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-100 text-red-500 hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors"><i className="ri-delete-bin-line" /></button>
                  </div>
                )}
              </motion.div>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-3 bg-white rounded-xl p-16 text-center">
                <i className="ri-box-3-line text-4xl text-gray-200 block mb-3" />
                <p className="text-sm text-gray-400">No products found</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-semibold text-gray-900">{editProduct ? 'Edit Product' : 'Add Product'}</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
              </div>
              <div className="space-y-3">
                <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Product name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                <textarea className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none" rows={3} placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={500} />
                <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white cursor-pointer" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {categories.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                </select>
                <div className="grid grid-cols-3 gap-3">
                  {[['1-2 Year', 'price_1_2_year'], ['3-5 Year', 'price_3_5_year'], ['Lifetime', 'price_lifetime']].map(([label, key]) => (
                    <div key={key}>
                      <label className="text-xs text-gray-400 mb-1 block">{label} ($)</label>
                      <input type="number" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400" value={form[key as keyof typeof form]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setShowModal(false)} className="flex-1 py-2 border border-surface-3 rounded-lg text-sm text-gray-600 hover:bg-surface-2 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name.trim()} className="flex-1 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                  {saving ? <><i className="ri-loader-4-line animate-spin" />Saving…</> : 'Save Product'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
